import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import {
  DisplayGrid,
  GridsterConfig,
  GridsterItem,
  GridType
} from 'angular-gridster2';
import { Subject, Subscription } from 'rxjs';
import { buffer, debounceTime } from 'rxjs/operators';
import { DashboardItem } from './dashboard-item';

export interface DashboardItemsMap {
  [identifier: string]: DashboardItem;
}

export interface DashboardPosition extends GridsterItem {
  componentIdentifier: string;
}

/**
 * `<labs-dashboard>` displays a 12 column dashboard layout that stacks vertically on `<= md` breakpoint.
 */
@Component({
  selector: 'labs-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent implements OnDestroy {
  @HostBinding('style.height') @Input() height = '100vh';

  @Input() items: DashboardItemsMap;

  @Input() positions: DashboardPosition[];

  @Output() changeItems = new EventEmitter<DashboardPosition[]>();

  /**
   * @ignore
   */
  itemChanges = new Subject<DashboardPosition>();

  /**
   * @ignore
   */
  itemChanges$ = this.itemChanges.asObservable();

  /**
   * @ignore
   */
  itemBatchChanges$ = this.itemChanges$.pipe(
    buffer(this.itemChanges$.pipe(debounceTime(50)))
  );

  /**
   * @ignore
   */
  private readonly columns = 12;

  /**
   * @ignore
   */
  private readonly rows = 100;

  /**
   * @ignore
   */
  private readonly subscription = new Subscription();

  /**
   * @ignore
   */
  options: GridsterConfig = {
    disableScrollHorizontal: true,
    disableWarnings: true,
    displayGrid: DisplayGrid.OnDragAndResize,
    draggable: { enabled: true },
    fixedRowHeight: 64,
    gridType: GridType.VerticalFixed,
    margin: 24,
    maxCols: this.columns,
    maxItemCols: this.columns,
    maxRows: this.rows,
    minCols: this.columns,
    minItemCols: 4,
    minItemRows: 4,
    mobileBreakpoint: 992,
    outerMargin: false,
    resizable: { enabled: true },
    swap: true,
    itemChangeCallback: (item) =>
      this.handleItemChange(item as DashboardPosition)
  };

  constructor() {
    this.subscription.add(
      this.itemBatchChanges$.subscribe((changes) =>
        this.changeItems.emit(changes)
      )
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private handleItemChange(item: DashboardPosition) {
    this.itemChanges.next(item);
  }
}
